import { Play, Pause, RotateCcw, CheckCircle, Heart, Activity, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

interface ExercisePlayerProps {
  onBack?: () => void;
}

export function ExercisePlayer({ onBack }: ExercisePlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [spo2, setSpo2] = useState(96);
  const [heartRate, setHeartRate] = useState(76);

  const totalDuration = 600; // 10 minutes in seconds
  const progressPercent = (currentTime / totalDuration) * 100;

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && currentTime < totalDuration) {
      interval = setInterval(() => {
        setCurrentTime((prev) => prev + 1);
        // Simulate real-time vitals changes
        setSpo2(95 + Math.floor(Math.random() * 3));
        setHeartRate(74 + Math.floor(Math.random() * 6));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentTime]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleComplete = () => {
    setIsPlaying(false);
    toast.success('Exercise session completed! Well done! 🎉', {
      description: 'Your progress has been saved.',
    });
    // Reset for next session
    setTimeout(() => {
      setCurrentTime(0);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div>
            <h1 className="text-primary-foreground">Diaphragmatic Breathing</h1>
            <p className="text-primary-foreground/80">10 minutes session</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Video Player Placeholder */}
        <Card className="overflow-hidden shadow-lg">
          <div className="aspect-video bg-gradient-to-br from-primary to-[#14CAF4] flex items-center justify-center">
            <div className="text-center text-primary-foreground">
              <Activity className="w-16 h-16 mx-auto mb-4" />
              <p>Exercise Video Player</p>
              <p className="text-primary-foreground/60">Follow the breathing pattern shown</p>
            </div>
          </div>
        </Card>

        {/* Timer and Progress */}
        <Card className="shadow-lg">
          <CardContent className="p-6 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-muted-foreground">Time Elapsed</p>
                <h2 className="text-primary">{formatTime(currentTime)}</h2>
              </div>
              <div className="text-right">
                <p className="text-muted-foreground">Remaining</p>
                <h2 className="text-accent">{formatTime(totalDuration - currentTime)}</h2>
              </div>
            </div>
            <Progress value={progressPercent} className="h-3" />
          </CardContent>
        </Card>

        {/* Real-time Vitals */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-primary mb-4">Real-time Vitals</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#14CAF4]/10 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-5 h-5 text-[#14CAF4]" />
                  <p className="text-muted-foreground">SpO₂</p>
                </div>
                <p className="text-[#14CAF4]">{spo2}%</p>
                <div className="w-full bg-secondary h-1 rounded-full mt-2">
                  <div
                    className="bg-[#14CAF4] h-1 rounded-full transition-all"
                    style={{ width: `${spo2}%` }}
                  />
                </div>
              </div>

              <div className="p-4 bg-accent/10 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="w-5 h-5 text-accent" />
                  <p className="text-muted-foreground">Heart Rate</p>
                </div>
                <p className="text-accent">{heartRate} bpm</p>
                <div className="w-full bg-secondary h-1 rounded-full mt-2">
                  <div
                    className="bg-accent h-1 rounded-full transition-all"
                    style={{ width: `${(heartRate / 120) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="shadow-lg bg-[#FEEBDA]">
          <CardContent className="p-6">
            <h3 className="text-primary mb-3">Instructions</h3>
            <ul className="space-y-2 text-foreground">
              <li className="flex gap-2">
                <span className="text-accent">1.</span>
                <span>Sit or lie down in a comfortable position</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent">2.</span>
                <span>Place one hand on your chest and the other on your abdomen</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent">3.</span>
                <span>Breathe in slowly through your nose, feeling your abdomen rise</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent">4.</span>
                <span>Exhale slowly through pursed lips</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Controls */}
        <div className="flex gap-3">
          <Button
            onClick={handlePlayPause}
            className="flex-1 bg-primary hover:bg-primary/90"
            size="lg"
          >
            {isPlaying ? (
              <>
                <Pause className="w-5 h-5 mr-2" />
                Pause
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                {currentTime > 0 ? 'Resume' : 'Start'}
              </>
            )}
          </Button>
          <Button onClick={handleReset} variant="outline" size="lg">
            <RotateCcw className="w-5 h-5" />
          </Button>
        </div>

        {currentTime >= totalDuration && (
          <Button
            onClick={handleComplete}
            className="w-full bg-accent hover:bg-accent/90"
            size="lg"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Mark as Complete
          </Button>
        )}
      </div>
    </div>
  );
}
